import React from 'react';

import { Button } from '@/design-system/button';
import { Input, InputRoot } from '@/design-system/input';
import { Popover, PopoverTrigger } from '@/design-system/popover';
import { FilterPopoverContent } from '@/components/FilterPopover';
import { SearchSelect } from '@/design-system/search-select';
import {
  DELIVERY_STATUS_LABELS,
  DeliveryStatus,
} from '@/services/api/deliveries';
import {
  useClientOptions,
  useDriverOptions,
  useOutletOptions,
} from './useDeliveryDropdownOptions';

export interface DeliveryFilterValue {
  status: DeliveryStatus[];
  codPaymentMethod: string[];
  businessUnit: string[];
  clientID: string;
  providerOutletIDs: string;
  driverID: string;
  startDate: string;
  endDate: string;
  scheduleStartDate: string;
  scheduleEndDate: string;
}

// Default business-unit scope. Used both to seed the filter on first load /
// reset and as the fallback when nothing is selected, so the list always
// stays scoped to these three units even when no businessUnit param is sent.
export const DEFAULT_BUSINESS_UNITS: string[] = [
  'X_DOCK',
  'SCHEDULED_INSTANT',
  '4_WHEEL',
];

// Default status scope sent when the user hasn't picked any status. Opening
// deliveries always requests this full operational set (note: EXPIRED is
// intentionally excluded) so the list is scoped consistently on first load.
export const DEFAULT_DELIVERY_STATUSES: DeliveryStatus[] = [
  'PENDING_PAYMENT',
  'QUEUEING',
  'ALLOCATING',
  'PENDING_PICKUP',
  'PICKING_UP',
  'PENDING_DELIVERY',
  'IN_DELIVERY',
  'PREPARING',
  'VERIFIED',
  'NOT_VERIFIED',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'PENDING_RETURN',
  'IN_RETURN',
  'RETURNED',
  'FAILED_IN_RETURN',
  'ON_HOLD',
  'DISPOSED',
];

export const EMPTY_DELIVERY_FILTERS: DeliveryFilterValue = {
  status: [],
  codPaymentMethod: [],
  businessUnit: [...DEFAULT_BUSINESS_UNITS],
  clientID: '',
  providerOutletIDs: '',
  driverID: '',
  startDate: '',
  endDate: '',
  scheduleStartDate: '',
  scheduleEndDate: '',
};

// Business unit is "active" only when it deviates from the default scope —
// the default three selected on load/reset shouldn't light up the badge.
const isDefaultBusinessUnit = (units: string[]): boolean =>
  units.length === DEFAULT_BUSINESS_UNITS.length &&
  DEFAULT_BUSINESS_UNITS.every((u) => units.includes(u));

export const countActiveDeliveryFilters = (v: DeliveryFilterValue): number =>
  Number(v.status.length > 0) +
  Number(v.codPaymentMethod.length > 0) +
  Number(!isDefaultBusinessUnit(v.businessUnit)) +
  Number(Boolean(v.clientID)) +
  Number(Boolean(v.providerOutletIDs)) +
  Number(Boolean(v.driverID)) +
  Number(Boolean(v.startDate || v.endDate)) +
  Number(Boolean(v.scheduleStartDate || v.scheduleEndDate));

// Full list, ordered to match back-office `deliveryStatusOptions` so ops
// staff see the same sequence in both apps.
export const STATUS_FILTERS: DeliveryStatus[] = [
  'PENDING_PAYMENT',
  'EXPIRED',
  'QUEUEING',
  'ALLOCATING',
  'PENDING_PICKUP',
  'PICKING_UP',
  'PREPARING',
  'PENDING_DELIVERY',
  'IN_DELIVERY',
  'COMPLETED',
  'NOT_VERIFIED',
  'VERIFIED',
  'FAILED',
  'IN_RETURN',
  'PENDING_RETURN',
  'FAILED_IN_RETURN',
  'ON_HOLD',
  'RETURNED',
  'CANCELLED',
  'DISPOSED',
];

const COD_METHODS: { key: string; label: string }[] = [
  { key: 'CASH', label: 'Cash' },
  { key: 'TRANSFER', label: 'Transfer' },
  { key: 'CASH_ADVANCE', label: 'Cash Advance' },
];

const BUSINESS_UNITS: { key: string; label: string }[] = [
  { key: 'X_DOCK', label: 'X Dock' },
  { key: 'SCHEDULED_INSTANT', label: 'Scheduled Instant' },
  { key: '4_WHEEL', label: '4 Wheel' },
];

interface DeliveryFilterPanelProps {
  open: boolean;
  /** Controls the Radix popover. Opening seeds the draft in the parent. */
  onOpenChange: (open: boolean) => void;
  /** Number of applied filters — shown as an ink pill on the trigger. */
  activeFilterCount: number;
  value: DeliveryFilterValue;
  onChange: (next: DeliveryFilterValue) => void;
  onApply: () => void;
  onReset: () => void;
}

const labelCls = 'mb-2 block text-[11px] font-medium text-text-sub-600';

const chipCls = (active: boolean) =>
  `rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors ${
    active
      ? 'border-accent text-accent'
      : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
  }`;

const toggle = <T,>(list: T[], v: T): T[] =>
  list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

const DeliveryFilterPanel: React.FC<DeliveryFilterPanelProps> = ({
  open,
  onOpenChange,
  activeFilterCount,
  value,
  onChange,
  onApply,
  onReset,
}) => {
  const clients = useClientOptions(open);
  const outlets = useOutletOptions(open, value.clientID || null);
  const drivers = useDriverOptions(open);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button tone="neutral" style="stroke" size="sm">
          <i className="ri-equalizer-2-line mr-1.5 text-[14px]" />
          Filter
          {activeFilterCount > 0 && (
            <span className="ml-1.5 rounded-md bg-bg-strong-950 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-text-white-0">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <FilterPopoverContent
        align="end"
        className="w-[420px] p-0"
        aria-label="Filter deliveries"
      >
        <div className="flex items-center justify-between border-b border-stroke-soft-200 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-text-strong-950">
            Filter deliveries
          </p>
          <button
            onClick={onReset}
            className="text-[11px] font-medium text-text-sub-600 hover:text-accent transition-colors"
            type="button"
          >
            Reset
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto space-y-5 px-4 py-3">
        <div>
          <p className={labelCls}>Status</p>
          <div className="flex flex-wrap gap-1">
            {STATUS_FILTERS.map((s) => {
              const active = value.status.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() =>
                    onChange({ ...value, status: toggle(value.status, s) })
                  }
                  className={chipCls(active)}
                >
                  {DELIVERY_STATUS_LABELS[s]}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className={labelCls}>COD method</p>
          <div className="flex flex-wrap gap-1">
            {COD_METHODS.map((m) => {
              const active = value.codPaymentMethod.includes(m.key);
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() =>
                    onChange({
                      ...value,
                      codPaymentMethod: toggle(value.codPaymentMethod, m.key),
                    })
                  }
                  className={chipCls(active)}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className={labelCls}>Business unit</p>
          <div className="flex flex-wrap gap-1">
            {BUSINESS_UNITS.map((b) => {
              const active = value.businessUnit.includes(b.key);
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() =>
                    onChange({
                      ...value,
                      businessUnit: toggle(value.businessUnit, b.key),
                    })
                  }
                  className={chipCls(active)}
                >
                  {b.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className={labelCls}>Client</p>
          <SearchSelect
            placeholder="All clients"
            options={clients.options}
            value={value.clientID}
            selectedLabelFallback={clients.labelFor(value.clientID)}
            onChange={(next) =>
              // Outlet is scoped to client — clear it when client changes
              // so a stale outlet doesn't filter against the wrong list.
              onChange({
                ...value,
                clientID: String(next),
                providerOutletIDs: '',
              })
            }
            onSearchChange={clients.setSearch}
            disableLocalFilter
            loading={clients.loading}
            emptyMessage={clients.error ?? 'No clients found'}
          />
        </div>

        <div>
          <p className={labelCls}>Outlet</p>
          <SearchSelect
            placeholder={
              value.clientID ? 'All outlets' : 'Select a client first'
            }
            options={outlets.options}
            value={value.providerOutletIDs}
            selectedLabelFallback={outlets.labelFor(value.providerOutletIDs)}
            onChange={(next) =>
              onChange({ ...value, providerOutletIDs: String(next) })
            }
            onSearchChange={outlets.setSearch}
            disableLocalFilter
            loading={outlets.loading}
            disabled={!value.clientID}
            emptyMessage={
              value.clientID
                ? (outlets.error ?? 'No outlets found')
                : 'Pick a client to see outlets'
            }
          />
        </div>

        <div>
          <p className={labelCls}>Mitra</p>
          <SearchSelect
            placeholder="All mitra"
            options={drivers.options}
            value={value.driverID}
            selectedLabelFallback={drivers.labelFor(value.driverID)}
            onChange={(next) => onChange({ ...value, driverID: String(next) })}
            onSearchChange={drivers.setSearch}
            disableLocalFilter
            loading={drivers.loading}
            emptyMessage={drivers.error ?? 'No drivers found'}
          />
        </div>

        <div>
          <p className={labelCls}>Created date</p>
          <div className="flex items-center gap-2">
            <InputRoot size="sm" className="flex-1">
              <Input
                type="date"
                value={value.startDate}
                onChange={(e) =>
                  onChange({ ...value, startDate: e.target.value })
                }
              />
            </InputRoot>
            <span className="text-xs text-text-soft-400">to</span>
            <InputRoot size="sm" className="flex-1">
              <Input
                type="date"
                value={value.endDate}
                onChange={(e) =>
                  onChange({ ...value, endDate: e.target.value })
                }
              />
            </InputRoot>
          </div>
        </div>

        <div>
          <p className={labelCls}>Schedule date</p>
          <div className="flex items-center gap-2">
            <InputRoot size="sm" className="flex-1">
              <Input
                type="date"
                value={value.scheduleStartDate}
                onChange={(e) =>
                  onChange({ ...value, scheduleStartDate: e.target.value })
                }
              />
            </InputRoot>
            <span className="text-xs text-text-soft-400">to</span>
            <InputRoot size="sm" className="flex-1">
              <Input
                type="date"
                value={value.scheduleEndDate}
                onChange={(e) =>
                  onChange({ ...value, scheduleEndDate: e.target.value })
                }
              />
            </InputRoot>
          </div>
        </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stroke-soft-200 px-4 py-3">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={onApply}>
            Apply
          </Button>
        </div>
      </FilterPopoverContent>
    </Popover>
  );
};

export default DeliveryFilterPanel;
